import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import html2pdf from "html2pdf.js";

import {
    Table,
    Input,
    Button,
    Space,
    Spin,
    Modal,
} from "antd";
import { message } from "../../components/antdMessageBridge";
import { SearchOutlined } from "@ant-design/icons";
import CommonObj from "../../CommonObj";
import Camera from "../../images/camera.svg";
import Html5QrcodePlugin from "../qrscanner/qrscanner";
import { getUTCDate } from "../../components/utcDate";
import { Html5QrcodeScanType } from "html5-qrcode";
import { useAuthToken } from "../../components/usecontext/AuthContext";
import { exportToExcel } from '../exporttoexcel/exporttoexcel';
import { useAuth } from "../../auth/AuthContext";
import { encryptTimestamp } from "../../components/commoncsrf/csrf-common";
import { renderCommonPaginationTotal } from "../../components/commonpagination/common-pagination";
import "../../components/commonpagination/common-pagination.css";
import { buildTimestampedFileName } from "../../components/cstDateFileName";

const Manifestqueue = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [totalRow, setTotalRow] = useState(1);
    const [scannedItemData, setScannedItemData] = useState(null);
    const [startTime, setStartTime] = useState("");
    const { accessToken } = useAuthToken();
    const [filteredData, setFilteredData] = useState([]);
    const { roleUserId } = useAuth();

    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [activeScanner, setActiveScanner] = useState(null);
    const [decodedResults, setDecodedResults] = useState([]);

    const searchInput = useRef(null);

    const [sortedInfo, setSortedInfo] = useState({});
    const [searchFilters, setsearchFilters] = useState({});

    const handleScan = (record) => {
        setScannedItemData(record);
        setActiveScanner("isbn");
    };

    const handleReset = (dataIndex, confirm, clearFilters) => {
        clearFilters();
        setsearchFilters((prev) => {
            const updated = { ...prev };
            delete updated[dataIndex];
            return updated;
        });
        fetchData({ pagination, sorter: sortedInfo, filters: { ...searchFilters, [dataIndex]: undefined } });
    };

    const handleExport = () => {
        const dynamicFileName = buildTimestampedFileName("table_data");
        exportToExcel(filteredData, columns, dynamicFileName);
    };

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    style={{ marginBottom: 8, display: "block" }}
                    onPressEnter={() => confirm()}
                />
                <Space>
                    <Button type="primary" size="small" onClick={() => confirm()}>Search</Button>
                    <Button size="small" onClick={() => {
                        handleReset(dataIndex, confirm, clearFilters);
                        confirm();
                    }}>Reset</Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />),
        filteredValue: searchFilters[dataIndex] ? [searchFilters[dataIndex]] : null,
        onFilter: (value, record) => record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current.select(), 100);
            }
        },
        sorter: true,
        sortOrder: sortedInfo.columnKey === dataIndex ? sortedInfo.order : null
    });

    const columns = [
        {
            title: "Rack No",
            dataIndex: "rackNo",
            key: "rackNo",
            sorter: (a, b) => (a.rackNo || "").localeCompare(b.rackNo || ""),
            ...getColumnSearchProps("rackNo"),
        },
        {
            title: "BID No",
            dataIndex: "bidNo",
            key: "bidNo",
            sorter: (a, b) => a.bidNo.localeCompare(b.bidNo),
            ...getColumnSearchProps("bidNo"),
        },
        {
            title: "Shelf ID",
            dataIndex: "shelfID",
            key: "shelfID",
            sorter: (a, b) => (a.shelfID || "").localeCompare(b.shelfID || ""),
            ...getColumnSearchProps("shelfID"),
        },
        {
            title: "Weight",
            dataIndex: "weight",
            key: "weight",
            sorter: (a, b) => (a.weight || "").localeCompare(b.weight || ""),
            ...getColumnSearchProps("weight"),
        },
        {
            title: "Actions",
            key: "actions",
            render: (_, record) => (
                <Space size="middle" onClick={() => handleScan(record)}>
                    {record.status === "Manifest Creation -TBI" && (
                        <img src={Camera} style={{ cursor: "pointer" }} alt="Camera icon"></img>
                    )}
                </Space>
            ),
        }
    ];

    const getRowClassName = (record, index) => {
        return index % 2 === 0 ? "odd-row" : "even-row";
    };

    const rowSelection = {
        onChange: (_, selectedRows) => {
            setSelectedRows(selectedRows);
            if (startTime == "") {
                if (selectedRows.length === 1 || selectedRows.length === totalRow) {
                    const startUTCTime = getUTCDate();
                    setStartTime(startUTCTime?.UTCDate);
                }
            }
            else if (selectedRows.length < 1) {
                setStartTime("");
            }
        },
    };

    useEffect(() => {
        const uniqueResults = filterUniqueScans(decodedResults);

        if (uniqueResults.length > 0 && activeScanner) {
            const latestResult = uniqueResults[uniqueResults.length - 1];
            setActiveScanner(null);
            setDecodedResults([]);
        }
    }, [decodedResults, activeScanner]);

    const handleDownload = async () => {
        if (selectedRows.length === 0) {
            message.warning("Please select at least one row to download.");
            return;
        }

        const hasEmptyRackno = selectedRows.some((row) => !row.rackNo);
        const bidNos = selectedRows.map((row) => row.bidNo);

        const endUTCTime = getUTCDate();
        if (hasEmptyRackno) {
            message.error(
                'Validation failed: Cannot download rows with an empty "Rack No."'
            );
            return;
        }
        const submissionData = {
            biDs: bidNos.join(", "),
            userId: roleUserId,
            startTime: startTime,
            endTime: endUTCTime?.UTCDate,
        };

        try {
            const response = await fetch(CommonObj.Endpoints.DownloadManifest, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "X-CSRF-Token": encryptTimestamp(),
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(submissionData),
            });

            if (!response.ok) {
                if (response.status == 404) {
                    message.error("File Not Found");
                }
                throw new Error(`Response status: ${response.status}`);
            }
            const result = await response.text();

            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = result;

            setLoading(true);

            const options = {
                margin: 0.5,
                filename: `manifest_${new Date().getTime()}.pdf`,
                image: { type: "jpeg", quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true },
                jsPDF: { unit: "in", format: "a4", orientation: "landscape" }
            };

            const pdfBlob = await html2pdf()
                .set(options)
                .from(tempDiv)
                .outputPdf("blob");
            const pdfUrl = URL.createObjectURL(pdfBlob);

            window.open(pdfUrl, "_blank");
            fetchData({ pagination, sorter: sortedInfo, filters: searchFilters });

            setLoading(false);
            document.body.removeChild(tempDiv);
        } catch (error) {
            console.error("Error submitting form:", error.message);
        }
        message.success(`Downloading data for ${selectedRows.length} item(s).`);
    };

    const handleTableChange = (newPagination, filters, sorter) => {
        const normalizeFilters = {};
        Object.keys(filters).forEach((key) => {
            if (filters[key] && filters[key].length > 0) {
                normalizeFilters[key] = filters[key][0];
            }
        });

        setsearchFilters(normalizeFilters);
        setSortedInfo(sorter);

        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize,
        }));

        fetchData({
            pagination: { current: newPagination.current, pageSize: newPagination.pageSize },
            sorter,
            filters: normalizeFilters,
        });
    };

    const saveScannedData = (scannedValue, userId) => {
        return new Promise((resolve, reject) => {
            if (!scannedValue || !userId) {
                reject(new Error("Missing required data."));
            } else {
                resolve({ message: "Scan data successfully saved!" });
            }
        });
    };

    const onNewScanResult = async (decodedText, decodedResult) => {
        setDecodedResults((prev) => [...prev, decodedResult]);
        if (scannedItemData) {
            try {
                setLoading(true);
                const result = await saveScannedData(decodedText, roleUserId);

                const submissionData = {
                    BID: scannedItemData.bidNo,
                    RackNo: decodedText,
                    UserId: roleUserId,
                };

                try {
                    const response = await fetch(
                        CommonObj.Endpoints.SaveManifestRackDetails,
                        {
                            method: "POST",
                            headers: {
                                "Authorization": `Bearer ${accessToken}`,
                                "X-CSRF-Token": encryptTimestamp(),
                                "Content-Type": "application/json"
                            },
                            body: JSON.stringify(submissionData),
                        }
                    );

                    if (!response.ok) {
                        throw new Error(`Response status: ${response.status}`);
                    }
                    fetchData({ pagination, sorter: sortedInfo, filters: searchFilters });

                } catch (error) {
                    console.error("Error submitting form:", error.message);
                }

                message.success(result.message);
            } catch (err) {
                message.error(`Scan failed: ${err.message}`);
            } finally {
                setLoading(false);
                setActiveScanner(null);
                setDecodedResults([]);
                setScannedItemData(null);
            }
        }
    };

    const fetchData = async ({ pagination: pag, filters, sorter }) => {
        const payload = {
            pageNo: pag?.current,
            pageSize: pag?.pageSize || 10,
            search: JSON.stringify(filters),
            orderByColumn: sorter?.field || "CreatedOn",
            orderBy: sorter?.order === "ascend" ? "ASC" : "DESC",
        };
        setLoading(true);
        try {
            const myHeaders = {
                "Authorization": `Bearer ${accessToken}`,
                "X-CSRF-Token": encryptTimestamp(),
                "Content-Type": "application/json",
            };
            const response = await fetch(CommonObj.Endpoints.GetManifestBooks, {
                method: "POST",
                headers: myHeaders,
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error("Network response was not ok");
            }

            const responseText = await response.text();
            let result;

            try {
                result = JSON.parse(responseText);
            } catch (e) {
                if (responseText.includes("No books found.")) {
                    result = [];
                } else {
                    throw new Error(responseText || 'Failed to parse API response.');
                }
            }

            const books = Array.isArray(result) ? result : [result];
            const formattedData = books.map(item => ({ ...item, key: item.bidNo }));

            setData(formattedData);

            const total = formattedData[0]?.totalRows || 0;
            setTotalRow(total);
            setPagination(prev => ({
                ...prev,
                current: pag?.current || prev.current,
                pageSize: pag?.pageSize || prev.pageSize,
                total,
            }));

            const startUTCTime = getUTCDate();
            setStartTime(startUTCTime?.UTCDate);

            setFilteredData(formattedData);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData({ pagination, sorter: sortedInfo, filters: searchFilters });
        setStartTime("");
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (loading) {
        return (
            <Spin size="large" style={{ margin: "20px auto" }} />
        );
    }

    function filterUniqueScans(results) {
        const filteredResults = [];
        if (results.length === 0) {
            return filteredResults;
        }
        filteredResults.push(results[0]);
        for (let i = 1; i < results.length; ++i) {
            if (results[i].decodedText !== results[i - 1].decodedText) {
                filteredResults.push(results[i]);
            }
        }
        return filteredResults;
    }

    return (
        <>
            <Modal
                title={`Scan Rack ID`}
                open={!!activeScanner}
                onCancel={() => setActiveScanner(null)}
                footer={null}
                destroyOnClose
                className="scan-model-container"
                width={1000}
            >
                <div className="scan-modal">
                    <Html5QrcodePlugin
                        fps={30}
                        qrbox={{ width: 1000, height: 400 }}
                        aspectRatio={2.8}
                        disableFlip={false}
                        qrCodeSuccessCallback={onNewScanResult}
                        supportedScanTypes={[Html5QrcodeScanType.SCAN_TYPE_CAMERA]}
                    />
                </div>
            </Modal>
            <div className="active-page-container">
                <div className="active-page-header-container">
                    <div className="active-page-header-title-container">
                        <h4>Manifest Creation</h4>
                    </div>
                    <div className="active-page-header-action-container">
                        <div className="active-page-header-button-container">
                            <button className="active-page-header-button" data-track="queue-manifest-export" onClick={handleExport}>
                                <span>
                                    <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 18 18"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <g clipPath="url(#clip0_875_15157)">
                                            <path
                                                d="M11.5447 3.04024L11.1433 3.48621L11.1433 3.48621L11.5447 3.04024ZM14.5138 5.71241L14.1124 6.15839L14.5138 5.71241ZM16.2405 7.61557L15.6924 7.85967L15.6924 7.85968L16.2405 7.61557ZM2.37868 15.6213L2.80294 15.1971H2.80294L2.37868 15.6213ZM15.6213 15.6213L15.1971 15.1971L15.6213 15.6213ZM10.5 16.5V15.9H7.5V16.5V17.1H10.5V16.5ZM1.5 10.5H2.1V7.5H1.5H0.9V10.5H1.5ZM16.5 10.1722H15.9V10.5H16.5H17.1V10.1722H16.5ZM11.5447 3.04024L11.1433 3.48621L14.1124 6.15839L14.5138 5.71241L14.9152 5.26643L11.9461 2.59426L11.5447 3.04024ZM16.5 10.1722H17.1C17.1 8.90922 17.1121 8.09787 16.7886 7.37147L16.2405 7.61557L15.6924 7.85968C15.8879 8.29862 15.9 8.80322 15.9 10.1722H16.5ZM14.5138 5.71241L14.1124 6.15839C15.1299 7.07417 15.4969 7.42073 15.6924 7.85967L16.2405 7.61557L16.7886 7.37147C16.4651 6.64507 15.8539 6.11131 14.9152 5.26643L14.5138 5.71241ZM7.52234 1.5V2.1C8.71153 2.1 9.15094 2.10926 9.54196 2.25931L9.75692 1.69914L9.97188 1.13897C9.325 0.890737 8.62066 0.9 7.52234 0.9V1.5ZM11.5447 3.04024L11.9461 2.59426C11.1337 1.8631 10.6187 1.38716 9.97188 1.13897L9.75692 1.69914L9.54196 2.25931C9.93306 2.40939 10.2639 2.69476 11.1433 3.48621L11.5447 3.04024ZM7.5 16.5V15.9C6.06882 15.9 5.0558 15.8987 4.28813 15.7955C3.53772 15.6946 3.11222 15.5063 2.80294 15.1971L2.37868 15.6213L1.95442 16.0456C2.52382 16.615 3.24476 16.866 4.12824 16.9848C4.99445 17.1013 6.10275 17.1 7.5 17.1V16.5ZM1.5 10.5H0.9C0.9 11.8973 0.898726 13.0055 1.01519 13.8718C1.13397 14.7552 1.38501 15.4762 1.95442 16.0456L2.37868 15.6213L2.80294 15.1971C2.49367 14.8878 2.30537 14.4623 2.20448 13.7119C2.10127 12.9442 2.1 11.9312 2.1 10.5H1.5ZM10.5 16.5V17.1C11.8973 17.1 13.0055 17.1013 13.8718 16.9848C14.7552 16.866 15.4762 16.615 16.0456 16.0456L15.6213 15.6213L15.1971 15.1971C14.8878 15.5063 14.4623 15.6946 13.7119 15.7955C12.9442 15.8987 11.9312 15.9 10.5 15.9V16.5ZM16.5 10.5H15.9C15.9 11.9312 15.8987 12.9442 15.7955 13.7119C15.6946 14.4623 15.5063 14.8878 15.1971 15.1971L15.6213 15.6213L16.0456 16.0456C16.615 15.4762 16.866 14.7552 16.9848 13.8718C17.1013 13.0055 17.1 11.8973 17.1 10.5H16.5ZM1.5 7.5H2.1C2.1 6.06882 2.10127 5.0558 2.20448 4.28813C2.30537 3.53772 2.49367 3.11222 2.80294 2.80294L2.37868 2.37868L1.95442 1.95442C1.38501 2.52382 1.13397 3.24476 1.01519 4.12824C0.898726 4.99445 0.9 6.10275 0.9 7.5H1.5ZM7.52234 1.5V0.9C6.11759 0.9 5.0039 0.898736 4.13418 1.01514C3.24765 1.13379 2.52428 1.38455 1.95442 1.95442L2.37868 2.37868L2.80294 2.80294C3.11175 2.49413 3.53856 2.30555 4.29336 2.20453C5.06497 2.10126 6.08378 2.1 7.52234 2.1V1.5Z"
                                                fill="currentColor"
                                            />
                                            <path
                                                opacity="0.5"
                                                d="M9.75 1.875V3.75C9.75 5.51777 9.75 6.40165 10.2992 6.95083C10.8483 7.5 11.7322 7.5 13.5 7.5H16.5"
                                                stroke="currentColor"
                                                strokeWidth="1.2"
                                            />
                                            <path
                                                opacity="0.5"
                                                d="M6.375 10.125L6.375 13.875M6.375 13.875L7.875 12.4688M6.375 13.875L4.875 12.4688"
                                                stroke="currentColor"
                                                strokeWidth="1.2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_875_15157">
                                                <rect width="18" height="18" fill="white" />
                                            </clipPath>
                                        </defs>
                                    </svg>
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
                <div className="active-page-body-container">
                    <div className="rounded-table-wrapper">
                        <Table
                            rowSelection={rowSelection}
                            columns={columns}
                            dataSource={data}
                            className="common-pagination common-grid"
                            pagination={{
                                ...pagination,
                                  pageSizeOptions: ["10", "20", "30", "40", "50", "100"],
                                showSizeChanger: true,
                                position: ["bottomRight"],
                                defaultPageSize: 10,
                                showTotal: (total, range) =>
                                    renderCommonPaginationTotal(total, range, {
                                        pagination,
                                        handleTableChange,
                                        searchFilters,
                                        sortedInfo
                                    }),
                            }}
                            rowKey={(record) => record.bidNo + record.isbn}
                            scroll={{ x: "max-content", y: "calc(100vh - 280px)" }}
                            rowClassName={getRowClassName}
                            onChange={handleTableChange}
                        />
                    </div>
                </div>
                <div className="active-page-footer-container">
                    <div className="active-page-action-container">
                        <div></div>
                        <div className="active-page-buttons-container">
                            <button className="active-page-download-button" onClick={handleDownload}>
                                <span>
                                    <svg
                                        width="21"
                                        height="20"
                                        viewBox="0 0 21 20"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M5.49984 14.9856C4.2053 14.9466 3.43316 14.8022 2.89874 14.2678C2.1665 13.5355 2.1665 12.357 2.1665 10C2.1665 7.64298 2.1665 6.46447 2.89874 5.73223C3.63097 5 4.80948 5 7.1665 5H13.8332C16.1902 5 17.3687 5 18.1009 5.73223C18.8332 6.46447 18.8332 7.64298 18.8332 10C18.8332 12.357 18.8332 13.5355 18.1009 14.2678C17.5665 14.8022 16.7944 14.9466 15.4998 14.9856"
                                            stroke="url(#paint0_linear_1306_12533)"
                                            strokeWidth="1.5"
                                        />
                                        <path
                                            opacity="0.5"
                                            d="M8 8.3335H5.5"
                                            stroke="url(#paint1_linear_1306_12533)"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                        />
                                        <path
                                            d="M16.3335 11.6665L4.66683 11.6665"
                                            stroke="url(#paint2_linear_1306_12533)"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                        />
                                        <path
                                            d="M15.5 11.6665V13.3332C15.5 15.6902 15.5 16.8687 14.7678 17.6009C14.0355 18.3332 12.857 18.3332 10.5 18.3332C8.14298 18.3332 6.96447 18.3332 6.23223 17.6009C5.5 16.8687 5.5 15.6902 5.5 13.3332V11.6665"
                                            stroke="url(#paint3_linear_1306_12533)"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                        />
                                        <path
                                            opacity="0.5"
                                            d="M15.4858 5.00008C15.4468 3.70554 15.3024 2.9334 14.768 2.39898C14.0357 1.66675 12.8572 1.66675 10.5002 1.66675C8.14319 1.66675 6.96468 1.66675 6.23244 2.39898C5.69803 2.9334 5.55365 3.70554 5.51465 5.00008"
                                            stroke="url(#paint4_linear_1306_12533)"
                                            strokeWidth="1.5"
                                        />
                                        <circle
                                            opacity="0.5"
                                            cx="14.6668"
                                            cy="8.33333"
                                            r="0.833333"
                                            fill="url(#paint5_linear_1306_12533)"
                                        />
                                        <path
                                            opacity="0.5"
                                            d="M13 13.75H8"
                                            stroke="url(#paint6_linear_1306_12533)"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                        />
                                        <path
                                            opacity="0.5"
                                            d="M11.3335 15.8335H8.00016"
                                            stroke="url(#paint7_linear_1306_12533)"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                        />
                                        <defs>
                                            <linearGradient
                                                id="paint0_linear_1306_12533"
                                                x1="19.3332"
                                                y1="9.9652"
                                                x2="2.43072"
                                                y2="12.8959"
                                                gradientUnits="userSpaceOnUse"
                                            >
                                                <stop stopColor="#033D9B" />
                                                <stop offset="1" stopColor="#29ACD5" />
                                            </linearGradient>
                                            <linearGradient
                                                id="paint1_linear_1306_12533"
                                                x1="5.425"
                                                y1="7.83626"
                                                x2="7.87158"
                                                y2="7.20087"
                                                gradientUnits="userSpaceOnUse"
                                            >
                                                <stop stopColor="#033D9B" />
                                                <stop offset="1" stopColor="#29ACD5" />
                                            </linearGradient>
                                            <linearGradient
                                                id="paint2_linear_1306_12533"
                                                x1="4.31683"
                                                y1="11.1693"
                                                x2="9.25335"
                                                y2="5.18644"
                                                gradientUnits="userSpaceOnUse"
                                            >
                                                <stop stopColor="#033D9B" />
                                                <stop offset="1" stopColor="#29ACD5" />
                                            </linearGradient>
                                            <linearGradient
                                                id="paint3_linear_1306_12533"
                                                x1="15.8"
                                                y1="14.9814"
                                                x2="5.60128"
                                                y2="16.5706"
                                                gradientUnits="userSpaceOnUse"
                                            >
                                                <stop stopColor="#033D9B" />
                                                <stop offset="1" stopColor="#29ACD5" />
                                            </linearGradient>
                                            <linearGradient
                                                id="paint4_linear_1306_12533"
                                                x1="15.7849"
                                                y1="3.32421"
                                                x2="6.28596"
                                                y2="6.27596"
                                                gradientUnits="userSpaceOnUse"
                                            >
                                                <stop stopColor="#033D9B" />
                                                <stop offset="1" stopColor="#29ACD5" />
                                            </linearGradient>
                                            <linearGradient
                                                id="paint5_linear_1306_12533"
                                                x1="15.5502"
                                                y1="8.32873"
                                                x2="13.8277"
                                                y2="8.50766"
                                                gradientUnits="userSpaceOnUse"
                                            >
                                                <stop stopColor="#033D9B" />
                                                <stop offset="1" stopColor="#29ACD5" />
                                            </linearGradient>
                                            <linearGradient
                                                id="paint6_linear_1306_12533"
                                                x1="7.85"
                                                y1="13.2528"
                                                x2="11.9634"
                                                y2="11.1162"
                                                gradientUnits="userSpaceOnUse"
                                            >
                                                <stop stopColor="#033D9B" />
                                                <stop offset="1" stopColor="#29ACD5" />
                                            </linearGradient>
                                            <linearGradient
                                                id="paint7_linear_1306_12533"
                                                x1="7.90016"
                                                y1="15.3363"
                                                x2="11.0095"
                                                y2="14.2596"
                                                gradientUnits="userSpaceOnUse"
                                            >
                                                <stop stopColor="#033D9B" />
                                                <stop offset="1" stopColor="#29ACD5" />
                                            </linearGradient>
                                        </defs>
                                    </svg>
                                    <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <g clipPath="url(#clip0_319_32002)">
                                            <path
                                                d="M4.99999 14.9856C3.70545 14.9466 2.93331 14.8022 2.39889 14.2678C1.66666 13.5355 1.66666 12.357 1.66666 10C1.66666 7.64298 1.66666 6.46447 2.39889 5.73223C3.13112 5 4.30963 5 6.66666 5H13.3333C15.6903 5 16.8689 5 17.6011 5.73223C18.3333 6.46447 18.3333 7.64298 18.3333 10C18.3333 12.357 18.3333 13.5355 17.6011 14.2678C17.0667 14.8022 16.2945 14.9466 15 14.9856"
                                                stroke="white"
                                                strokeWidth="1.5"
                                            />
                                            <path
                                                opacity="0.5"
                                                d="M7.5 8.33331H5"
                                                stroke="white"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                            />
                                            <path
                                                d="M15.8333 11.6667L4.16668 11.6667"
                                                stroke="white"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                            />
                                            <path
                                                d="M15 11.6667V13.3334C15 15.6904 15 16.8689 14.2678 17.6011C13.5355 18.3334 12.357 18.3334 10 18.3334C7.64298 18.3334 6.46447 18.3334 5.73223 17.6011C5 16.8689 5 15.6904 5 13.3334V11.6667"
                                                stroke="white"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                            />
                                            <path
                                                opacity="0.5"
                                                d="M14.9856 5.00002C14.9466 3.70548 14.8022 2.93334 14.2678 2.39892C13.5355 1.66669 12.357 1.66669 10 1.66669C7.64298 1.66669 6.46446 1.66669 5.73223 2.39892C5.19781 2.93334 5.05344 3.70548 5.01443 5.00002"
                                                stroke="white"
                                                strokeWidth="1.5"
                                            />
                                            <circle
                                                opacity="0.5"
                                                cx="14.1667"
                                                cy="8.33333"
                                                r="0.833333"
                                                fill="white"
                                            />
                                            <path
                                                opacity="0.5"
                                                d="M12.5 13.75H7.5"
                                                stroke="white"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                            />
                                            <path
                                                opacity="0.5"
                                                d="M10.8333 15.8333H7.50001"
                                                stroke="white"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                            />
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_319_32002">
                                                <rect width="20" height="20" fill="white" />
                                            </clipPath>
                                        </defs>
                                    </svg></span>Download Manifest
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

Manifestqueue.propTypes = {};

export default Manifestqueue;

